import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { api } from '../../utils/api';
import API_ENDPOINTS from '../../config/apiConfig';
import { formatCurrencyRounded, currencySymbol } from '../../utils/currency';

const DailyForecastModal = ({
  isOpen,
  onClose,
  account,
  month,
  year,
  orgId,
  locationId,
  currency = 'USD',
  selectedDriver,
  onSave
}) => {
  const [dailyData, setDailyData] = useState([]);
  const [savedDailyData, setSavedDailyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedCells, setSavedCells] = useState(new Set());
  const [editableRowData, setEditableRowData] = useState([]);
  const [localInputValues, setLocalInputValues] = useState({});
  const inputRefs = React.useRef({});

  // Fetch daily data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchDailyData = async () => {
      setIsLoading(true);
      try {
        // Fetch daily detail data from SQL view (filtered by month/year)
        // This includes both actuals and saved forecast amounts
        const detailResponse = await api.get(API_ENDPOINTS.FORECAST_DETAIL_DAILY, {
          params: {
            orgId,
            locationId,
            chartOfAccountId: account.chartOfAccountId,
            year,
            month: month + 1 // API expects 1-based month
          }
        });

        // Fetch saved forecast data from SQL financialForecast table
        const forecastResponse = await api.get(API_ENDPOINTS.FINANCIAL_FORECAST, {
          params: {
            orgId,
            locationId,
            chartOfAccountId: account.chartOfAccountId,
            year,
            month: month + 1 // API expects 1-based month
          }
        });

        setDailyData(detailResponse.data || []);
        setSavedDailyData(forecastResponse.data || []);
      } catch (error) {
        console.error('Error fetching daily data:', error);
        setDailyData([]);
        setSavedDailyData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDailyData();
  }, [isOpen, orgId, locationId, account.chartOfAccountId, year, month]);

  // Get the number of days in the month
  const daysInMonth = useMemo(() => {
    return new Date(year, month + 1, 0).getDate();
  }, [year, month]);

  // Generate initial row data for each day of the month
  useEffect(() => {

    const rows = [];

    // Create a map of saved daily forecast data by date
    const savedDataByDate = {};
    savedDailyData.forEach(entry => {
      if (!entry.forecastDate) return;
      const date = new Date(entry.forecastDate);
      const dateKey = date.toISOString().split('T')[0];
      savedDataByDate[dateKey] = {
        forecastAmount: entry.forecastAmount,
        notes: entry.notes,
        id: entry.id
      };
    });

    // Create a map of daily detail data by date
    const detailDataByDate = {};
    dailyData.forEach(entry => {
      if (!entry.forecastDate) return;
      const date = new Date(entry.forecastDate);
      const dateKey = date.toISOString().split('T')[0];
      detailDataByDate[dateKey] = entry;
    });

    // Calculate monthly budget total from all daily entries
    const monthlyBudgetTotal = dailyData.reduce((sum, entry) => sum + (entry.dailyBudgetAmount || 0), 0);
    // Divide monthly budget by number of days to get daily budget
    const dailyBudgetAmount = daysInMonth > 0 ? monthlyBudgetTotal / daysInMonth : 0;

    // Get today's date for past/future comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      date.setHours(0, 0, 0, 0);
      const dateKey = date.toISOString().split('T')[0];
      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });

      // Determine if this is a past date (before today - today is NOT past)
      const isPastDate = date < today;

      const detailData = detailDataByDate[dateKey] || {};
      const savedData = savedDataByDate[dateKey] || {};

      // Get effective forecast amount from SQL view
      // All driver logic is handled in vw_ForecastDetailDaily.effectiveForecastAmount
      // Fallback: if SQL view not yet deployed, use dailyRevenueAmount for Static Entry
      let initialForecastAmount = 0;
      if (detailData.effectiveForecastAmount !== undefined && detailData.effectiveForecastAmount !== null) {
        initialForecastAmount = parseFloat(detailData.effectiveForecastAmount) || 0;
      } else if (selectedDriver === 'Static Entry') {
        // Fallback until SQL is deployed: use imported revenue
        const importedRevenue = parseFloat(detailData.dailyRevenueAmount) || 0;
        const savedForecast = savedData.forecastAmount;
        if (isPastDate && importedRevenue !== 0) {
          initialForecastAmount = importedRevenue;
        } else if (savedForecast !== undefined && savedForecast !== null) {
          initialForecastAmount = savedForecast;
        } else {
          initialForecastAmount = importedRevenue;
        }
      }

      // Use calculatedOtbForecast from SQL view - already handles past vs future logic
      // (Room revenue for past days, OTB forecast for today and future)
      const calculatedOtbForecast = detailData.calculatedOtbForecast || 0;

      rows.push({
        id: `day_${day}`,
        date: dateKey,
        day,
        dayOfWeek,
        dailyBudget: dailyBudgetAmount, // Use calculated daily budget
        dailyActual: detailData.dailyActualAmount || 0,
        dailyRevenue: detailData.dailyRevenueAmount || 0,
        dailyRoomsAvailable: detailData.dailyRoomsAvailable || 0,
        dailyRoomsOccupied: detailData.dailyRoomsOccupied || 0,
        dailyOccupancyPercent: detailData.dailyOccupancyPercent || 0,
        otbForecastRevenue: calculatedOtbForecast,  // From SQL: room revenue for past, OTB for today+future
        rmsForecastRevenue: detailData.rmsForecastRevenue || 0,   // From SQL view
        forecastAmount: initialForecastAmount,
        notes: savedData.notes || '',
        forecastId: savedData.id || null,
        isPastDate: isPastDate  // Track for potential styling
      });
    }

    setEditableRowData(rows);
  }, [daysInMonth, year, month, dailyData, savedDailyData, selectedDriver]);

  // Calculate totals from editable row data
  const totalsRow = useMemo(() => {
    if (!editableRowData || editableRowData.length === 0) return null;

    const totals = {
      id: 'totals-row',
      day: 'TOTAL',
      dayOfWeek: '',
      dailyBudget: editableRowData.reduce((sum, row) => sum + (row.dailyBudget || 0), 0),
      dailyActual: editableRowData.reduce((sum, row) => sum + (row.dailyActual || 0), 0),
      dailyRevenue: editableRowData.reduce((sum, row) => sum + (row.dailyRevenue || 0), 0),
      dailyRoomsAvailable: editableRowData.reduce((sum, row) => sum + (row.dailyRoomsAvailable || 0), 0),
      dailyRoomsOccupied: editableRowData.reduce((sum, row) => sum + (row.dailyRoomsOccupied || 0), 0),
      dailyOccupancyPercent: '', // Don't calculate average
      otbForecastRevenue: editableRowData.reduce((sum, row) => sum + (row.otbForecastRevenue || 0), 0),
      rmsForecastRevenue: editableRowData.reduce((sum, row) => sum + (row.rmsForecastRevenue || 0), 0),
      forecastAmount: editableRowData.reduce((sum, row) => sum + (row.forecastAmount || 0), 0),
      notes: ''
    };

    return [totals];
  }, [editableRowData]);

  const formatCurrency = useCallback((value) => {
    if (!value && value !== 0) return `${currencySymbol(currency)}0`;
    return formatCurrencyRounded(value, currency);
  }, [currency]);

  // Handle cell edit
  const handleCellEdit = useCallback((row, field, newValue) => {
    // Update local input values
    const cellKey = `${row.id}_${field}`;
    setLocalInputValues(prev => ({
      ...prev,
      [cellKey]: newValue
    }));

    // Update the local row data state for real-time totals
    setEditableRowData(prevRows => {
      return prevRows.map(r => {
        if (r.id === row.id) {
          return {
            ...r,
            [field]: field === 'forecastAmount' ? (parseFloat(newValue) || 0) : newValue
          };
        }
        return r;
      });
    });

    // Mark cell as recently edited for visual feedback
    setSavedCells(prev => new Set([...prev, cellKey]));

    setTimeout(() => {
      setSavedCells(prev => {
        const updated = new Set(prev);
        updated.delete(cellKey);
        return updated;
      });
    }, 500);
  }, []);

  // Handle Enter key navigation
  const handleKeyDown = useCallback((e, currentRow, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Find current row index
      const currentRowIndex = editableRowData.findIndex(r => r.id === currentRow.id);

      // Determine next field and row
      let nextRow = currentRow;
      let nextField = currentField;
      let nextRowIndex = currentRowIndex;

      // Navigation logic
      if (currentField === 'forecastAmount') {
        // Move to next row's forecastAmount field
        nextRowIndex = currentRowIndex + 1;
        if (nextRowIndex < editableRowData.length) {
          nextRow = editableRowData[nextRowIndex];
          nextField = 'forecastAmount';
        } else {
          return; // Already at last row
        }
      } else if (currentField === 'notes') {
        // Move to next row's forecastAmount field
        nextRowIndex = currentRowIndex + 1;
        if (nextRowIndex < editableRowData.length) {
          nextRow = editableRowData[nextRowIndex];
          nextField = 'forecastAmount';
        } else {
          return; // Already at last row
        }
      }

      // Focus the next input
      const nextKey = `${nextRow.id}_${nextField}`;
      if (inputRefs.current[nextKey]) {
        inputRefs.current[nextKey].focus();
      }
    }
  }, [editableRowData]);


  // Handle save - uses editableRowData which contains real-time edits
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Prepare daily forecast entries for bulk save from editableRowData
      // Include ALL rows (even zeros) to properly save the complete daily breakdown
      const dailyForecasts = editableRowData.map(row => ({
        orgId,
        locationId,
        chartOfAccountId: account.chartOfAccountId,
        forecastDate: row.date,
        forecastAmount: row.forecastAmount,  // SQL table expects forecastAmount, not dailyAmount
        notes: row.notes || ''
      }));

      // Save daily data in bulk to SQL database
      if (dailyForecasts.length > 0) {
        await api.post(API_ENDPOINTS.FINANCIAL_FORECAST, dailyForecasts);
      }

      // Notify parent component of save (this will refresh the monthly grid)
      if (onSave) {
        onSave();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Error saving daily forecasts:', error);
      alert('Failed to save daily forecasts. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const monthName = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long' });

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 99999 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Daily Forecast Entry - {monthName} {year}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {account.accountName} ({account.accountNumber})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={isSaving}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 pt-6 pb-2" style={{ minHeight: 0, overflow: 'auto' }}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-auto max-h-[70vh]">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Day</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Day</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Budget</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Actual</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Avail Rooms</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Occ Rooms</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Occ %</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">OTB</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">RMS</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Forecast</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {editableRowData.map((row) => {
                      const isWeekend = row.dayOfWeek === 'Sat' || row.dayOfWeek === 'Sun';
                      const cellKey = row.id;

                      return (
                        <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                          {/* Day Number */}
                          <td className="px-4 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">
                            {row.day}
                          </td>

                          {/* Day of Week */}
                          <td className={`px-4 py-3 text-sm whitespace-nowrap ${isWeekend ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                            {row.dayOfWeek}
                          </td>

                          {/* Budget */}
                          <td className="px-4 py-3 text-right text-sm text-gray-700 whitespace-nowrap">
                            {formatCurrency(row.dailyBudget)}
                          </td>

                          {/* Actual */}
                          <td className="px-4 py-3 text-right text-sm text-gray-700 whitespace-nowrap">
                            {formatCurrency(row.dailyActual)}
                          </td>

                          {/* Revenue */}
                          <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">
                            {formatCurrency(row.dailyRevenue)}
                          </td>

                          {/* Available Rooms */}
                          <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">
                            {row.dailyRoomsAvailable.toLocaleString()}
                          </td>

                          {/* Occupied Rooms */}
                          <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">
                            {row.dailyRoomsOccupied.toLocaleString()}
                          </td>

                          {/* Occupancy % */}
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                <div
                                  className="bg-blue-500 h-1.5 rounded-full"
                                  style={{ width: `${Math.min(100, row.dailyOccupancyPercent)}%` }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 w-10 text-right">{row.dailyOccupancyPercent.toFixed(1)}%</span>
                            </div>
                          </td>

                          {/* OTB Forecast Revenue */}
                          <td className="px-4 py-3 text-right text-sm text-blue-700 whitespace-nowrap">
                            {formatCurrency(row.otbForecastRevenue)}
                          </td>

                          {/* RMS Forecast Revenue */}
                          <td className="px-4 py-3 text-right text-sm text-purple-700 whitespace-nowrap">
                            {formatCurrency(row.rmsForecastRevenue)}
                          </td>

                          {/* Forecast Amount */}
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <input
                              ref={(el) => inputRefs.current[`${cellKey}_forecastAmount`] = el}
                              type="number"
                              value={localInputValues[`${cellKey}_forecastAmount`] !== undefined ? localInputValues[`${cellKey}_forecastAmount`] : row.forecastAmount}
                              onChange={(e) => handleCellEdit(row, 'forecastAmount', parseFloat(e.target.value) || 0)}
                              onKeyDown={(e) => handleKeyDown(e, row, 'forecastAmount')}
                              onFocus={() => {
                                const currentValue = localInputValues[`${cellKey}_forecastAmount`] !== undefined ? localInputValues[`${cellKey}_forecastAmount`] : row.forecastAmount;
                                if (parseFloat(currentValue) === 0) {
                                  setLocalInputValues(prev => ({
                                    ...prev,
                                    [`${cellKey}_forecastAmount`]: ''
                                  }));
                                }
                              }}
                              onBlur={(e) => {
                                if (e.target.value === '') {
                                  handleCellEdit(row, 'forecastAmount', 0);
                                }
                              }}
                              className={`w-28 px-2 py-1 text-sm font-bold text-right text-green-800 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                savedCells.has(`${cellKey}_forecastAmount`) ? 'bg-green-100' : 'bg-orange-50 border-orange-200'
                              }`}
                              step="0.01"
                              min="0"
                            />
                          </td>

                          {/* Notes */}
                          <td className="px-4 py-3">
                            <input
                              ref={(el) => inputRefs.current[`${cellKey}_notes`] = el}
                              type="text"
                              value={localInputValues[`${cellKey}_notes`] !== undefined ? localInputValues[`${cellKey}_notes`] : (row.notes || '')}
                              onChange={(e) => handleCellEdit(row, 'notes', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, row, 'notes')}
                              placeholder="Add notes..."
                              className={`w-full px-2 py-1 text-sm text-gray-600 border rounded focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                                savedCells.has(`${cellKey}_notes`) ? 'bg-green-100' : 'bg-white border-gray-200'
                              }`}
                            />
                          </td>
                        </tr>
                      );
                    })}

                    {/* Totals Row */}
                    {totalsRow && totalsRow[0] && (
                      <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                        <td className="px-4 py-3 text-sm font-bold text-gray-900">{totalsRow[0].day}</td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(totalsRow[0].dailyBudget)}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(totalsRow[0].dailyActual)}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{formatCurrency(totalsRow[0].dailyRevenue)}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{totalsRow[0].dailyRoomsAvailable.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-900">{totalsRow[0].dailyRoomsOccupied.toLocaleString()}</td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-right text-sm text-blue-900">{formatCurrency(totalsRow[0].otbForecastRevenue)}</td>
                        <td className="px-4 py-3 text-right text-sm text-purple-900">{formatCurrency(totalsRow[0].rmsForecastRevenue)}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-green-900">{formatCurrency(totalsRow[0].forecastAmount)}</td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-600">
            Monthly Total: <span className="font-bold text-gray-900">{formatCurrency(totalsRow?.[0]?.forecastAmount || 0)}</span>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isSaving ? 'Saving...' : 'Save Daily Forecast'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React portal to render modal at document body level to escape z-index stacking context
  return ReactDOM.createPortal(modalContent, document.body);
};

export default DailyForecastModal;
